/**
 * Pai Gow Poker bot. Betting is casual (a small multiple of the minimum, never more than a tenth of the stack). The hands are set the
 * house way, the same fixed heuristic the dealer uses; weaker bots occasionally take a different legal split. The bot only ever reads
 * its own seat's view.
 */
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { paiGowIsLegalSplit, type PaiGowAction, type PaiGowLegal, type PaiGowView } from './engine.js';

function chooseSet(view: PaiGowView, ctx: BotContext): PaiGowAction {
  if (view.hand.length !== 7) return { type: 'houseWay' };
  // Easier bots misjudge the split now and then by picking any legal one.
  const slip = ctx.difficulty === 'BEGINNER' ? 0.2 : ctx.difficulty === 'NORMAL' ? 0.05 : 0;
  if (chance(ctx, slip)) {
    for (let tries = 0; tries < 20; tries++) {
      const i = ctx.rng.int(7);
      const j = ctx.rng.int(7);
      if (i === j) continue;
      const low = [view.hand[i]!, view.hand[j]!];
      if (paiGowIsLegalSplit(view.hand.filter((_, k) => k !== i && k !== j), low)) return { type: 'set', low };
    }
  }
  return { type: 'houseWay' };
}

export const paiGowBot: BotEngine<PaiGowView, PaiGowLegal, PaiGowAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'set') return chooseSet(view, ctx);
    return this.chooseBet!(view, legal, ctx);
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bet') return this.handleTimeout(view, legal, ctx);
    if (legal.canSitOut && chance(ctx, 0.03)) return { type: 'sitOut' };
    const me = view.seat === null ? null : view.players[view.seat];
    const multiple = 1 + ctx.rng.int(ctx.difficulty === 'BEGINNER' ? 4 : 3);
    const cap = me ? Math.max(legal.min, Math.floor(me.chips / 10)) : legal.min;
    return { type: 'bet', amount: Math.max(legal.min, Math.min(legal.min * multiple, cap, legal.max)) };
  },

  evaluateState(view) {
    const me = view.seat === null ? null : view.players[view.seat];
    return me ? Math.max(0, Math.min(1, me.chips / (view.config.minBet * 200))) : 0;
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view) {
    return view.phase === 'SETTING' ? { type: 'houseWay' } : { type: 'sitOut' };
  },
};
